import Papa from 'papaparse';
import type {
  ClassPerfOverviewResponse, ClassPerfBySubjectRow,
  ClassPerfExamRow, ClassPerfLeaderboardResponse, ClassPerfTrendsResponse,
  AssessmentCycle,
} from '../../../api/types';
import { guardRows } from '../../../lib/csv';

export function buildOverviewCsv(d: ClassPerfOverviewResponse): string {
  const kpis = Papa.unparse({
    fields: ['metric', 'value'],
    data: guardRows([
      ['cohort_size',        d.cohort_size],
      ['students_with_data', d.students_with_data],
      ['attempts_count',     d.attempts_count],
      ['avg_score',          d.avg_score ?? ''],
      ['median_score',       d.median_score ?? ''],
      ['p25',                d.p25 ?? ''],
      ['p75',                d.p75 ?? ''],
    ]),
  }, { newline: '\n' });
  const hist = Papa.unparse({
    fields: ['bucket', 'count'],
    data: guardRows(d.histogram.map((b) => [b.bucket, b.count])),
  }, { newline: '\n' });
  return `${kpis}\n\n${hist}`;
}

export function buildBySubjectCsv(rows: ClassPerfBySubjectRow[]): string {
  return Papa.unparse({
    fields: ['subject_id', 'name', 'avg_score', 'attempts_count', 'students_with_data'],
    data: guardRows(rows.map((r) => [
      r.id, r.name, r.avg_score ?? '', r.attempts_count, r.students_with_data,
    ])),
  }, { newline: '\n' });
}

export function buildExamsCsv(rows: ClassPerfExamRow[]): string {
  return Papa.unparse({
    fields: [
      'exam_id', 'name', 'subject', 'scope', 'created',
      'students_took', 'avg_score', 'avg_duration_minutes',
      'score_distribution', 'delta_vs_prior_on_same_subject',
    ],
    data: guardRows(rows.map((r) => [
      r.id, r.name, r.subject?.name ?? '', r.scope, r.created,
      r.students_took, r.avg_score, r.avg_duration_minutes ?? '',
      r.score_distribution.map((b) => b.count).join('|'),
      r.delta_vs_prior_on_same_subject ?? '',
    ])),
  }, { newline: '\n' });
}

export function buildLeaderboardCsv(d: ClassPerfLeaderboardResponse): string {
  return Papa.unparse({
    fields: ['section_id', 'display', 'rank', 'avg_score', 'students_with_data', 'attempts_count'],
    data: guardRows(d.sections.map((s) => [
      s.id, s.display, s.rank ?? '', s.avg_score ?? '',
      s.students_with_data, s.attempts_count,
    ])),
  }, { newline: '\n' });
}

export function buildTrendsCsv(d: ClassPerfTrendsResponse): string {
  return Papa.unparse({
    fields: ['week_start', 'avg_score', 'attempts_count', 'students_with_data'],
    data: guardRows(d.series.map((p) => [
      p.week_start, p.avg_score ?? '', p.attempts_count, p.students_with_data,
    ])),
  }, { newline: '\n' });
}

export function buildCycleCsv(cycle: AssessmentCycle, studentNames: Record<number, string>): string {
  const base = cycle.baseline_mastery.cells ?? {};
  const after = cycle.gain.cells ?? {};
  const data: Array<Array<string | number>> = [];
  for (const sid of cycle.target_student_ids) {
    for (const ch of cycle.target_chapters) {
      const b = base[String(sid)]?.[String(ch.chapter_id)];
      const a = after[String(sid)]?.[String(ch.chapter_id)];
      data.push([studentNames[sid] ?? String(sid), ch.name, b?.pct ?? '', a?.after_pct ?? '', a?.delta ?? '']);
    }
  }
  return Papa.unparse({ fields: ['student', 'chapter', 'baseline_pct', 'after_pct', 'delta'], data: guardRows(data) }, { newline: '\n' });
}

export function downloadCsv(filename: string, csv: string): void {
  // UTF-8 BOM so Excel reads non-Latin text (Amharic) correctly.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
